import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import InputField from "../../components/InputField";
import { useNavigate } from "react-router-dom";
import { getpendings, getPendingsByDateRange } from "../../services/paymentService";
import Button from "../../components/Button";

type Payment = {
  id: number; 
  customerName: string;
  customerId: string;
  loanId: string;
  loan_code:string;
  dueDate: string;
  amount: number; 
  paidAmount?: number;
  pendingAmount?: number;
  totalDue?:number;
  status?: "Upcoming" | "late" | "Paid" | "Partially Paid" | "Late Paid"| string;
};

const TAB_KEY = "payments.activeTab"; // sessionStorage key

const Payments: React.FC = () => {
  // load last selected tab from sessionStorage (fallback to "today")
const [activeTab, setActiveTab] = useState<"today" | "week" | "month" | "custom">(() => {
  const saved = sessionStorage.getItem(TAB_KEY);
  if (saved === "today" || saved === "week" || saved === "month" || saved === "custom") return saved;
  return "today";
});

  const [query, setQuery] = useState("");
const [allPayments, setAllPayments] = useState<{
  today: Payment[];
  week: Payment[];
  month: Payment[];
  custom: Payment[];
}>({ today: [], week: [], month: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showFilter, setShowFilter] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [hasCustomFilter, setHasCustomFilter] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  //  Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowFilter(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  // --- TAB TRACK STATE & REFS ---
const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({
  today: null,
  week: null,
  month: null,
  custom: null,
});
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});

  // Save activeTab to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(TAB_KEY, activeTab);
  }, [activeTab]);

  // compute indicator position & width
  const updateIndicator = () => {
    const btn = tabRefs.current[activeTab];
    const container = tabsContainerRef.current;
    if (!btn || !container) {
      setIndicatorStyle({});
      return;
    }

    // btn.offsetLeft is relative to the parent (container)
    const width = btn.offsetWidth;
    const left = btn.offsetLeft;
    

    setIndicatorStyle({
      width: `${width}px`,
      transform: `translateX(${left}px)`,
    });
  };

  // Recacl on activeTab change, after layout / payments load and on resize
  useEffect(() => {
    requestAnimationFrame(updateIndicator);
    const id = window.setTimeout(updateIndicator, 100);
    return () => clearTimeout(id);
  }, [activeTab, allPayments]);

  useEffect(() => {
    const onResize = () => {
      requestAnimationFrame(updateIndicator);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function parseSafeNumber(value: any) {
    if (value == null) return 0;
    const n = Number(String(value).replace(/[^0-9.\-]+/g, ""));
    return Number.isFinite(n) ? n : 0;
  }

  // Fetch payments (unchanged)
  useEffect(() => {
    async function fetchPayments() {
      try {
        setLoading(true);
        const data = await getpendings();

        const mapList = (list: any[]): Payment[] =>
          (list || []).map((p) => ({
            id: p.schedule_id,
            customerName: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || "Unknown",
            customerId: p.customer_code ?? "-",
            loanId: (p.loan_id ?? "").toString(),
            loan_code:p.loan_code?? "",
            dueDate: p.due_date,
            amount: parseSafeNumber(p.total_due),
            paidAmount: parseSafeNumber(p.paid_amount),
            pendingAmount: parseSafeNumber(p.pending_amount),
            status: p.payment_status,
            totalDue : p.total_due,
          }));

        setAllPayments({
          today: mapList(data?.today_due_list || []),
          week: mapList(data?.weekly_due_list || []),
          month: mapList(data?.monthly_due_list || []),
          custom: mapList(data?.custom_due_list || []),
        });
      } catch (err) {
        console.error("Failed to fetch payments", err);
      } finally {
        setLoading(false);
      }
    }
    fetchPayments();
  }, []);

  const handleApplyFilter = async () => {
  if (!fromDate || !toDate) {
    alert("Please select both dates");
    return;
  }

  try {
    setLoading(true);
    const data = await getPendingsByDateRange(fromDate, toDate);

    const mapList = (list: any[]): Payment[] =>
      (list || []).map((p) => ({
        id: p.schedule_id,
        customerName: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || "Unknown",
        customerId: p.customer_code ?? "-",
        loanId: (p.loan_id ?? "").toString(),
        loan_code:p.loan_code?? "",
        dueDate: p.due_date,
        amount: parseSafeNumber(p.total_due),
        paidAmount: parseSafeNumber(p.paid_amount),
        pendingAmount: parseSafeNumber(p.pending_amount),
        status: p.payment_status,
        totalDue: p.total_due,
      }));

    setAllPayments((prev) => ({
      ...prev,
      custom: mapList(data?.custom_due_list || []),
    }));
     setHasCustomFilter(true);
    setActiveTab("custom");
    setShowFilter(false);
  } catch (err) {
    console.error("Failed to fetch filtered payments", err);
    alert("Error fetching filtered payments");
  } finally {
    setLoading(false);
  }
};

const handleClearFilter = () => {
  setFromDate("");
  setToDate("");
  setAllPayments((prev) => ({
    ...prev,
    custom: [],
  }));
  setHasCustomFilter(false);
  setActiveTab("today");
  setShowFilter(false);
};


// Update filteredByTab logic
const filteredByTab = useMemo(() => {
  if (activeTab === "today") return allPayments.today;
  if (activeTab === "week") return allPayments.week;
  if (activeTab === "custom") return allPayments.custom;
  return allPayments.month;
}, [allPayments, activeTab]);

  // Filter by search query
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return filteredByTab;
    return filteredByTab.filter(
      (p) =>
        p.customerName.toLowerCase().includes(q) ||
        p.loanId.toLowerCase().includes(q) ||
        p.customerId.toLowerCase().includes(q)
    );
  }, [filteredByTab, query]);

  // Send only required fields. If Partially Paid -> send pendingAmount, else send amount (total_due)
  const handlePay = (payment: Payment) => {
    // const amountToPay = payment.status?.toLowerCase().includes("part")
    //   ? payment.pendingAmount ?? payment.amount
    //   : payment.amount;

    // extra safety: persist tab right before navigate
    sessionStorage.setItem(TAB_KEY, activeTab);
navigate("/loan-dues", { state: { loan_id: payment.loanId } });
    // navigate("/payments/single", {
    //   state: {
    //     payment: {
    //       raw: {
    //         schedule_id: payment.id,
    //         loan_id: payment.loanId,
    //         loan_code:payment.loan_code,
    //         total_due: payment.amount,
    //         paid_amount: payment.paidAmount,
    //         pending_amount: payment.pendingAmount,
    //         due_date: payment.dueDate,
    //         payment_status: payment.status,
    //         first_name: payment.customerName.split(" ")[0] ?? "",
    //         last_name: payment.customerName.split(" ").slice(1).join(" ") ?? "",
    //         customer_code: payment.customerId,
    //       },
    //       amount: payment.amount,
    //       dueDate: payment.dueDate,
    //       remainingNumeric: amountToPay,
    //     },
    //     prefillAmountNumeric: amountToPay,
    //   },
    // });
  };

  const handleView = (payment: Payment) => {
    sessionStorage.setItem(TAB_KEY, activeTab);
    navigate("/loan-dues", { state: { loan_id: payment.loanId } });
  };


  return (
    <div className="p-4 md:p-6 lg:p-8 bg-white rounded-2xl shadow-sm mt-3 ">
      {/* Top controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 sticky top-0 pt-2  bg-white z-10">
        {/* Tabs */}
        <div className="flex items-center overflow-x-auto scrollbar-hide">
          <div
            ref={tabsContainerRef}
            className="relative bg-gray-100 rounded-full gap-2 p-1 flex items-center text-nowrap"
          >
            {/* Sliding Track */}
            <span
              aria-hidden
              className="absolute top-1 left-1 h-[calc(100%-0.5rem)] bg-[#002949] rounded-full transition-all duration-300 ease-in-out shadow"
              style={indicatorStyle}
            />
            {[
              { key: "today", label: "Today" },
              { key: "week", label: "This Week" },
              { key: "month", label: "This Month" },
                ...(hasCustomFilter ? [{ key: "custom", label: "Custom Range" }] : []),
            ].map((t) => {
              const active = activeTab === (t.key as any);
              return (
                <button
                  key={t.key}
                  ref={(el) => { tabRefs.current[t.key] = el; }}
                  onClick={() => setActiveTab(t.key as any)} // this will also persist via useEffect
                  className={`relative z-10 px-4 py-2 rounded-full text-sm font-semibold transition ${
                    active ? "text-white" : "text-gray-700 hover:text-indigo-600 cursor-pointer"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
         
          </div>
       

        {/* Search */}
        <div className="flex items-center gap-2 ">
          <div className="relative w-full md:w-72">
            <InputField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className="w-full focus:outline-none focus:ring-2 focus:ring-sky-300 transition"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon icon="mdi:magnify" width={18} className="text-slate-400" />
            </div>
          </div>
          <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Filter Button */}
      <button
        title="filter"
        onClick={() => setShowFilter(!showFilter)}
        className="flex items-center gap-1 border rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition cursor-pointer"
      >
        <Icon icon="mdi:filter-variant" className="text-gray-500" width={18} />
        Filter
      </button>

      {/* Dropdown */}
      {showFilter && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg p-4 z-50">
          <h3 className="text-sm font-semibold text-gray-600 mb-3">
            Filter by Date
          </h3>

          <div className="flex flex-col gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">From Date</label>
              <InputField
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                // className="w-full border rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-sky-300 focus:outline-none"
              />
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">To Date</label>
              <InputField
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                // className="w-full border rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-sky-300 focus:outline-none"
              />
            </div>

            <div className="flex gap-2 mt-2 justify-end">
               <Button
          variant="secondary"
          onClick={handleClearFilter}
          // className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 text-sm font-medium rounded-lg py-1.5 transition"
        >
          Clear
        </Button>
        <Button
          onClick={handleApplyFilter}
          // className="flex-1 bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium rounded-lg py-1.5 transition"
        >
          Apply
        </Button>
       

      </div>
          </div>
        </div>
      )}
    </div>
      </div>
       </div>
       <div className="flex justify-end"> 
       <p className="text-sm text-gray-400"> 
        {/* Filters :  */}
        {filtered.length} {filtered.length === 1 ? " payment" : " payments"}</p>
        </div>

      {/* Cards grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading payments...</div>
      ) : filtered.length === 0 ? (
        <div className="col-span-full text-center py-12 text-gray-500">No payments found.</div>
      ) : (
        <div className="px-1 space-y-8">
  {/* ---- Pending/Active Loans ---- */}
  <section>
    <h2 className="text-lg font-semibold text-gray-800 mb-3">Pending </h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {filtered
        .filter((p) => p.status?.toLowerCase() !== "paid" &&
    p.status?.toLowerCase() !== "late paid")
        .map((p) => (
          <article
            key={p.id}
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition h-full flex flex-col"
          >
            <div className="p-4 md:p-5 md:pb-0 flex items-start gap-4">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                  style={{ background: stringToColor(p.customerName) }}
                >
                  {p.customerName.charAt(0).toUpperCase()}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">{p.customerName}</h4>
                    <div className="text-xs text-gray-500">{p.customerId}</div>
                  </div>

                  <div className="text-right">
                    <div className="text-sm font-semibold">
                      {p.status?.toLowerCase().includes("part")
                        ? `Pending: ${formatCurrency(p.pendingAmount ?? p.amount)}`
                        : formatCurrency(p.amount)}
                    </div>
                    <div className={`text-xs mt-1 ${statusColorClass(p.status)}`}>{p.status ?? ""}</div>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs text-gray-500">Loan Code </div>
                    <div className="text-sm font-medium text-gray-700">{p.loan_code}</div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500">Due Date</div>
                    <div className="text-sm font-medium text-gray-700">{formatDateNice(p.dueDate)}</div>
                  </div>
                </div>

                <div className="mt-3 text-sm text-gray-600 flex justify-between pb-2">
                  {p.status?.toLowerCase().includes("part") ? (
                    <>
                      <div>Paid: {formatCurrency(p.paidAmount ?? 0)}</div>
                      <div>Totaldue: {formatCurrency(p.totalDue ?? 0)}</div>
                    </>
                  ) : (
                    <div className="opacity-0 select-none">No breakdown</div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-auto p-5 md:pt-0 pt-0 ">
              <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleView(p)}
                  className="text-sm text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition cursor-pointer"
                >
                  View
                </button>
                <button
                  onClick={() => handlePay(p)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg shadow hover:bg-indigo-700 transition cursor-pointer"
                >
                  <Icon icon="fluent:payment-32-regular" width={16} />
                  Pay Now
                </button>
              </div>
            </div>
          </article>
        ))}
      {filtered.filter((p) => p.status?.toLowerCase() !== "paid" &&
    p.status?.toLowerCase() !== "late paid").length === 0 && (
        <div className="text-gray-500 text-sm p-4">No active or pending loans.</div>
      )}
    </div>
  </section>

  {/* ---- Paid Loans ---- */}
  <section>
    <h2 className="text-lg font-semibold text-gray-800 mb-3">Paid List</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {filtered
        .filter((p) => p.status?.toLowerCase() === "paid" ||
    p.status?.toLowerCase() === "late paid")
        .map((p) => (
          <article
            key={p.id}
            className="bg-green-50 rounded-xl shadow-sm border border-green-100 overflow-hidden hover:shadow-md transition h-full flex flex-col"
          >
            <div className="p-4 md:p-5 flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                style={{ background: stringToColor(p.customerName) }}
              >
                {p.customerName.charAt(0).toUpperCase()}
              </div>

              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-800">{p.customerName}</h4>
                <div className="text-xs text-gray-500 mb-1">{p.customerId}</div>
                <div className="text-sm font-semibold text-green-600">Paid: {formatCurrency(p.amount)}</div>
                <div className="text-xs text-green-500 mt-1">{p.status}</div>
              </div>
                <button
                  onClick={() => handleView(p)}
                  className="text-sm text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition cursor-pointer"
                >
                  View
                </button>
                
            </div>
            {/* <div className="flex justify-end p-2 pe-4">
                 <p className="text-blue-500"> Download Pdf</p>
                </div> */}
          </article>
        ))}
      {filtered.filter((p) => p.status?.toLowerCase() === "paid" ||
    p.status?.toLowerCase() === "late paid").length === 0 && (
        <div className="text-gray-500 text-sm p-4">No paid loans yet.</div>
      )}
    </div>
  </section>
</div>

      )}
    </div>
  );
};

export default Payments;

/* -------------------- helpers -------------------- */

function stringToColor(str: string) {
  const colors = ["#3B82F6", "#10B981", "#F97316", "#EF4444", "#8B5CF6", "#F59E0B"];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function formatCurrency(n: number | undefined) {
  const val = Number.isFinite(Number(n)) ? Number(n) : 0;
  return val.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
}

function formatDateNice(dateStr: string) {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function statusColorClass(status?: string) {
  if (!status) return "text-gray-500";
  if (status.toLowerCase() === "paid") return "text-green-600";
  if (status.toLowerCase() === "late paid") return "text-green-600";
  if (status.toLowerCase() === "overdue") return "text-red-600";
  if (status.toLowerCase().includes("part")) return "text-yellow-600"; // Partially Paid
  return "text-yellow-600"; // Pending / Upcoming
}
